---
title: "Your First Trade on {{BRAND}}: Step-by-Step"
slug: your-first-trade
shortTitle: "Your First Trade"
description: "A complete walkthrough of one small BTC/USDT trade: choosing a size, setting leverage and a stop loss in USDT, reading the order summary, then managing and closing the trade."
level: beginner
category: trading
course: beginner
order: 12
topics: [cfds, risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: true
popular: false
cover: /academy/img/covers/your-first-trade.svg
faqs:
  - q: "Should I practise on a demo account first?"
    a: "It is a sensible first step. A demo account uses virtual funds, so you can learn the order panel, stop losses and closing a position without risking money. Keep in mind that trading real money feels different, so start small when you switch to live."
  - q: "Is the Amount in the order panel the money I am risking?"
    a: "No. In USDT mode, Amount is the full value of the position. The money locked to hold it is shown as Margin required, and the amount you could lose depends on how far the price moves and where your stop loss is."
  - q: "Why is my position slightly negative as soon as I open it?"
    a: "Because of the spread. A buy opens at the Ask, but its profit and loss is measured at the Bid, the price you could sell at right now. The gap between the two, plus the trade fee, means every new position starts a little in the red."
  - q: "Can I close only part of my position?"
    a: "The Close button closes the whole position. On desktop, Instant Trade has buttons to close 25% or 50% of a position. Another approach is to open your position in smaller pieces so you can close them separately."
  - q: "Will my stop loss always close the trade at exactly my price?"
    a: "Not always. A stop loss becomes a market order when the price reaches it, so in a fast market or after a price gap it can fill at a worse price than the one you set."
---

Reading about trading only gets you so far. At some point you place an order, watch it move, and close it, and that first round trip teaches you more about spreads, margin and stop losses than any definition. The goal of a first trade is not to make money. It is to learn the controls while the stakes are small enough that a mistake costs very little.

This walkthrough follows one small Bitcoin trade on {{BRAND}} from start to finish: choosing the size, setting leverage and a stop loss in USDT, reading the order summary, then watching, adjusting and closing the position. Every number is illustrative, and every label matches what you will see on screen.

<Warning title="Start small, and expect to make mistakes">

Every market on {{BRAND}} is a leveraged CFD. Leverage magnifies losses as well as gains, and many traders lose money. Practise on a demo account if you can, keep your first live trades tiny, and only use money you can afford to lose.

</Warning>

<CTA label="Open BTC/USDT" to="/trade/BTC" />

<KeyTakeaways>

- **Decide the plan before you click.** Direction, size and the most you will lose in USDT come first; the buttons come second.
- **Amount is position value, not risk.** Margin required is what the trade locks; your stop loss decides what you can lose.
- **Read the order summary every time.** Margin required, Fee, Free margin after and Value of $1 move tell you exactly what you are about to do.
- **Review the result in Closed Positions.** Net PnL includes fees and swap, and the Reason column tells you how the trade ended.

</KeyTakeaways>

## Before you start

Run through this short checklist:

- **Practise first if you can.** On a computer, the top bar has a **Live / Demo** switch. Pressing **Demo** requests a demo account with {{DEMO_BALANCE}}, which you can reset later from the Wallet. Everything in this guide works the same way there.
- **For a live trade,** your account needs to be verified and funded. [Account Setup, Verification and Deposits](/academy/article/account-setup-deposits) covers that.
- **Know the basics.** If stop losses are new to you, read [Stop Loss and Take Profit](/academy/article/stop-loss-take-profit) first.

{/* TODO: verify with platform — demo account availability and approval wording. */}

## The plan for this trade

Decide everything before you touch the order panel. Here is the plan we will follow.

<Example title="A small BTC/USDT buy (illustrative prices)">

Account: **1,000 USDT**. BTC/USDT shows Bid 59,990 and Ask 60,000.

- **Direction:** Buy, at market. A buy fills at the Ask, about 60,000.
- **Size:** a position worth 1,200 USDT, which is 1,200 ÷ 60,000 = **0.02 BTC**.
- **Leverage:** 5x, so the margin required is 1,200 ÷ 5 = **240 USDT**.
- **Maximum loss:** 20 USDT, which is 2% of the account. At 0.02 BTC, each 1 USDT of price movement is worth 0.02 USDT, so the stop loss sits 20 ÷ 0.02 = 1,000 below entry, at **59,000**.
- **Profit target:** 40 USDT, so the take profit sits 40 ÷ 0.02 = 2,000 above entry, at **62,000**.

</Example>

Notice the order of decisions. The stop loss came from the loss you can accept, not from a guess about where the price is going. That habit matters far more than which market you pick.

## Placing the trade, step by step

<Steps>
<Step title="Open the BTC/USDT trade screen">

Choose **Trade** in the top bar and pick BTC/USDT from the asset list, or use **Search markets** (press / on a keyboard). The chart sits in the middle and the order panel on the right. Check the **Bid** and **Ask**.

</Step>
<Step title="Choose Buy and the Market tab">

Press **Buy** at the top of the order panel and make sure the **Market** tab is selected. A market order fills straight away at the current price; the **Limit / Stop** tab is for orders that wait for a price you choose.

</Step>
<Step title="Enter the size in USDT">

Set the size toggle to **USDT** and type 1,200 into **Amount**. Under the box you will see the equivalent in BTC (about 0.02) and in lots. An amount that doesn't buy a whole number of lots is rounded down to the lot step, so check the lots line: that is the size you will get. Avoid the 0–100% slider for now: 100% means the largest position your margin allows, not a sensible size.

</Step>
<Step title="Set the leverage">

Press the **Leverage** button, move the **Adjust Leverage** slider to 5x and press **Confirm**. You can choose up to {{MAX_LEVERAGE}}, but you do not have to. The pop-up also shows the largest position you could open at that setting, and reminds you that your whole account backs your positions.

</Step>
<Step title="Add your stop loss and take profit">

Switch on **TP / SL**. Type 20 into **Loss (USDT)** and 40 into **Profit (USDT)**. The panel fills in **SL Price** (about 59,000) and **TP Price** (about 62,000) and shows each one's distance from entry in percent.

</Step>
<Step title="Read the order summary">

Under the button, check **Est. entry** (the Ask), **Margin required** (240 USDT), **Fee**, **Free margin after** and **Value of $1 move** (0.02). If any number surprises you, stop and find out why.

</Step>
<Step title="Place the order">

The big button spells out the whole order, for example "Buy $1,200.00 of BTC/USDT". If your amount was rounded down to the lot step, the button shows the smaller value you are actually buying. Press it once. A message confirms the position has opened.

</Step>
</Steps>

<Screenshot name="first-trade-order-panel" alt="The order panel set to Buy, Market, 1,200 USDT, 5x leverage, with TP / SL switched on" caption="The order panel ready to go: size, leverage, TP / SL and the order summary. Bitcoin's price here differs from the example, so the figures do too." />

The real trade fee is {{TRADE_FEE}}; in the examples below we use a made-up 0.1% of the position value each way, purely to show the arithmetic: 1,200 × 0.1% = 1.20 USDT to open. **Free margin after** would then read 1,000 − 240 − 1.20 = **758.80 USDT**.

## Watching your position

Your trade now appears in the **Open Positions** tab under the chart, with its Entry Price, Mark Price, Margin, **PnL (ROE)** and **TP / SL**.

The PnL is worked out at the price you could close at right now, which for a buy is the Bid. Straight after opening, with the Bid at 59,990, it shows (59,990 − 60,000) × 0.02 = **−0.20 USDT**. That is the spread at work.

**ROE** is the PnL as a percentage of the margin. If the Bid rises to 60,480, PnL is (60,480 − 60,000) × 0.02 = **+9.60 USDT**, and ROE is 9.60 ÷ 240 = **4.0%**. A high ROE on a small trade can feel exciting; judge the trade by the USDT amount instead.

<Screenshot name="positions-tabs" alt="The Open Positions tab with a BTC/USDT buy among other trades, showing entry price, mark price, margin, PnL (ROE) and TP / SL" caption="Open Positions: your trade's live PnL, margin and TP / SL." />

## Changing your stop loss or take profit

Plans can change once a trade is moving. Press **Edit TP / SL** on the position (the pencil icon on desktop) to open a dialog with the take profit and stop loss prices. Type a new price and press **Save**. Leaving a field empty removes that level. On desktop you can also drag the TP and SL lines on the chart, with a confirmation before the change is sent.

Say the Bid has reached 60,600. You could move the stop loss up to 60,000, your entry, so the trade can no longer lose much more than its fees. That is often called moving to **break-even**. There is no automatic trailing stop, so to follow the price you move the stop yourself.

{/* TODO: verify with platform — automatic trailing stops are not currently offered. */}

<Screenshot name="edit-tp-sl-dialog" alt="The Edit TP / SL dialog with take profit and stop loss price fields" caption="Edit TP / SL: change a level, or leave it empty to remove it." />

<Warning>

Move a stop loss closer to protect a trade, never further away to avoid a loss. Widening or removing your stop turns a planned 20 USDT loss into one with no limit.

</Warning>

## Closing the trade

A position closes in one of three ways: the take profit is hit, the stop loss is hit, or you press **Close** in the Actions column. Close shuts the whole position at the current price.

<Example title="Closing by hand (illustrative prices and fees)">

You press Close with the Bid at 60,700.

- Gross result: (60,700 − 60,000) × 0.02 = **+14.00 USDT**
- Fees at the made-up 0.1%: 1.20 to open, plus 0.02 × 60,700 × 0.1% ≈ 1.21 to close
- Net: 14.00 − 1.20 − 1.21 = **+11.59 USDT**, before any swap

</Example>

If you hold a position past {{SWAP_HOUR}}, a daily swap is added too, as explained in [Funding, Swaps and Trading Costs](/academy/article/trading-costs-swaps-fees).

## Reading Closed Positions

Open the **Closed Positions** tab to see the finished trade with its Exit Price, the time it **Closed**, the **Reason** (Closed, Take profit, Stop loss or Liquidation), **Fees**, **Swap** and **Net PnL**. Hovering over Net PnL shows how it adds up: the gross result, open fee, close fee and swap.

<Screenshot name="closed-positions-tab" alt="The Closed Positions tab showing exit price, reason, fees, swap and net PnL" caption="Closed Positions: the final result after fees and swap." />

<ProTip>

Write the trade down while it is fresh: why you entered, where your stop and target were, how it ended, and what you would change. A simple log turns every trade, winning or losing, into a lesson. [Building a Trading Plan and Journal](/academy/article/trading-plan-and-journal) shows how.

</ProTip>

## Conclusion

Your first trade is practice with real controls. Plan the trade before you open the panel, size it from the loss you can accept, set the stop loss with **Loss (USDT)**, and read the order summary before you press the button. Then watch how the spread, the Bid and the fee show up in your PnL, adjust your stop only in your favor, and finish by reading the result in Closed Positions. Repeat that routine on small sizes until it feels automatic.
